import {
  type IsoDateTime,
  Link,
  Notification,
  useModal,
} from '@omnipresentgroup/design-system';
import {
  invoicesPath,
  useCancelContractorPaymentMutation,
  useContractorPayment,
} from '@omnipresentgroup/domains';
import {
  type ContractorInvoice,
  ContractorInvoiceStateEnum,
} from '@omnipresentgroup/types';
import { useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { CONTRACTOR_INVOICES_PAYMENT_PATH } from '../../../../constants';
import { ExpiredPaymentModal } from './ExpiredPaymentModal';
import { NotExpiredPaymentModal } from './NotExpiredPaymentModal';

// TODO: move this function to OmniDS dates
const isDateInThePast = (date: IsoDateTime) =>
  DateTime.fromISO(date).toMillis() < DateTime.now().toMillis();

type Props = {
  invoice: ContractorInvoice;
};

export const InvoiceNotification = ({ invoice }: Props) => {
  const queryClient = useQueryClient();

  const { data: paymentData } = useContractorPayment({
    id: invoice.paymentId,
    enabled: !!invoice.paymentId,
  });

  const {
    mutate: cancelPayment,
    isSuccess: isCancelSuccess,
    isError: isCancelError,
  } = useCancelContractorPaymentMutation({ id: invoice.paymentId });

  const navigate = useNavigate();
  const { modalProps } = useModal({});
  const [modalOpen, setModalOpen] = useState(false);

  const showNotification =
    invoice.state === ContractorInvoiceStateEnum.IN_PROGRESS;

  const isPaymentExpired =
    paymentData && isDateInThePast(paymentData?.transferDetailsAvailableUntil);

  const title =
    'We’re expecting your transfer to arrive soon. We will pay your contractor as soon as it does.';
  const description = 'Didn’t make the transfer yet?';

  const PayLink = () => (
    <Link onClick={() => setModalOpen(true)} size="14">
      Pay now
    </Link>
  );

  const isBulkPayment = useMemo(
    () =>
      paymentData?.quote?.invoiceBreakdown &&
      paymentData.quote.invoiceBreakdown.length > 1,
    [paymentData?.quote.invoiceBreakdown],
  );

  useEffect(() => {
    const invalidateInvoicesQueries = async () => {
      await queryClient.invalidateQueries({
        queryKey: [invoicesPath],
      });
    };
    if (isCancelSuccess) {
      setModalOpen(false);
      invalidateInvoicesQueries();
      paymentData?.quote.invoiceBreakdown &&
        navigate(`${CONTRACTOR_INVOICES_PAYMENT_PATH}`, {
          state: {
            invoicesIds: paymentData.quote.invoiceBreakdown.map(
              (invoiceQuote) => invoiceQuote.invoiceId,
            ),
          },
        });
    }
    if (isCancelError) {
      setModalOpen(false);
    }
  }, [
    isCancelError,
    isCancelSuccess,
    navigate,
    paymentData?.quote.invoiceBreakdown,
    queryClient,
  ]);

  return showNotification ? (
    <>
      <Notification
        id={`${invoice.id}-notification`}
        title={title}
        description={description}
        link={<PayLink />}
      />
      {isPaymentExpired ? (
        <ExpiredPaymentModal
          modalProps={{
            ...modalProps,
            open: modalOpen,
            onOpenChange: setModalOpen,
          }}
          handleCancel={() => {
            setModalOpen(false);
          }}
          handleSubmit={() => {
            cancelPayment();
          }}
          isBulkPayment={isBulkPayment}
        />
      ) : (
        <NotExpiredPaymentModal
          modalProps={{
            ...modalProps,
            open: modalOpen,
            onOpenChange: setModalOpen,
          }}
          handleDone={() => {
            setModalOpen(false);
          }}
          invoiceId={invoice.id}
          isBulkPayment={isBulkPayment}
        />
      )}
    </>
  ) : null;
};
